import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import OtherHousesOutlinedIcon from "@mui/icons-material/OtherHousesOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Divider, Modal, Stack, Typography, alpha } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { SxProps } from "@mui/system";
import type { JSX } from "react";
import { Trans, useTranslation } from "react-i18next";

import "./translations";

import { ImmoScout24Icon, ImmoScout24Logo, SprengnetterIcon, SprengnetterLogo } from "@/assets";
import { StepIcon, UploadCloud } from "@/assets/icons";
import { NotificationPanel } from "@/components";
import { DotProgress } from "@/components/dotProgress";
import { colors } from "@/theme/tokens/colors";

const styles: Record<string, SxProps<Theme>> = {
  centeredModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
    },
  },
  modalGrid: {
    display: "grid",
    gridTemplateRows: "min-content min-content 1fr min-content",
    width: "800px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    outline: "none",
  },
  progressContainer: {
    justifyContent: "center",
    alignItems: "start",
    height: "100%",
    p: "30px",
  },
  footerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "25px",
  },
  errorIcon: {
    color: "#9F0037",
    fontSize: 30,
    alignSelf: "start",
  },
};

const getTransferCompletedDividerStyle = (theme: Theme, isError?: boolean): SxProps<Theme> => ({
  width: "50px",
  border: isError
    ? `1px solid ${alpha(theme.palette.error.main, 0.5)}`
    : `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
});

interface DataTransferModalProps {
  open: boolean;
  isDataLoading: boolean;
  isError: boolean | undefined;
  error?: {
    header?: string;
    text?: string;
  };
}

export const DataTransferModal = ({ open, isDataLoading, isError, error }: DataTransferModalProps): JSX.Element => {
  const { t } = useTranslation("ValuationReport");
  const theme = useTheme();

  return (
    <Modal open={open} sx={styles.centeredModal}>
      <Box sx={styles.modalGrid}>
        <Stack direction="row" spacing={3} p={"30px"} alignItems={"center"}>
          <UploadCloud sx={{ fontSize: "36px" }} />
          <Typography variant="modalHeader">{t("dataTransfer.headerText")}</Typography>
        </Stack>
        <Divider />
        <Box sx={styles.progressContainer}>
          <Stack spacing={2}>
            <Typography variant="modalTitle" pt={"10px"} pb={"30px"}>
              {t("dataTransfer.title")}
            </Typography>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <PersonOutlineOutlinedIcon sx={{ fontSize: "28px" }} color="primary" />
              <Typography>
                <Trans
                  i18nKey="dataTransfer.accountSuccessfullyLinked"
                  ns="ValuationReport"
                  components={{
                    bold: <strong />,
                  }}
                />
              </Typography>
            </Stack>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <OtherHousesOutlinedIcon sx={{ fontSize: "28px" }} color="primary" />
              <Typography>
                <Trans
                  i18nKey="dataTransfer.dataIsBeingTransferred"
                  ns="ValuationReport"
                  components={{
                    bold: <strong />,
                  }}
                />
              </Typography>
            </Stack>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <SettingsOutlinedIcon sx={{ fontSize: "28px" }} color="primary" />
              <Typography>
                <Trans
                  i18nKey="dataTransfer.serviceIsBeingSetUp"
                  ns="ValuationReport"
                  components={{
                    bold: <strong />,
                  }}
                />
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Stack alignItems="center" p={"30px"} spacing={3}>
          <Box sx={{ ...styles.footerContainer, pb: isDataLoading ? 0 : "22px" }}>
            <ImmoScout24Logo sx={{ width: "103px", height: "30px" }} />
            <ImmoScout24Icon />
            {isDataLoading && <DotProgress dotColor={colors.brand.blue} />}
            {!isDataLoading && (
              <Stack direction="row" spacing={0} alignItems="center">
                <Divider orientation="horizontal" sx={getTransferCompletedDividerStyle(theme, !!isError)} />
                {isError ? <HighlightOffOutlinedIcon sx={{ fontSize: "50px" }} color="error" /> : <StepIcon />}
                <Divider orientation="horizontal" sx={getTransferCompletedDividerStyle(theme, !!isError)} />
              </Stack>
            )}
            <SprengnetterIcon />
            <SprengnetterLogo sx={{ width: "141px", height: "30px", marginLeft: "-17px" }} />
          </Box>
          {isDataLoading && <Typography variant="subText">{t("dataTransfer.bePatientForMoment")}</Typography>}
        </Stack>
        {error && (
          <Box sx={{ pb: "30px" }}>
            <NotificationPanel
              type="error"
              icon={<ErrorRoundedIcon sx={styles.errorIcon} />}
              contentSx={{ maxWidth: "580px", justifySelf: "center" }}
              header={error.header}
              text={error.text}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
